import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createEffect } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { payPaymentMethodsRepository } from '~/repositories/payPaymentMethodsRepository';
import type { PayPaymentMethodsRepository } from '~/repositories/payPaymentMethodsRepository';
import type {
  ConnectedAccountDto,
  ConnectedAccountDtoPaginationDto,
  CreateAchPaymentMethodRequestDto,
  CreateCardPaymentMethodRequestDto,
  CreatePaymentMethodRequestDto,
  CreateTokenPaymentMethodRequestDto,
  ExchangeAuthorizedMethodsRequestDto,
  ExchangedAuthorizedMethodsDto,
  GetPaymentMethodsParams,
  PaymentMethodDto,
  ReactivatePaymentMethodRequestDto,
  RequestConnectedAccountRequestDto,
  StartPaymentMethodSetupRequestDto,
  UpdatePaymentMethodRequestDto,
} from '~/swagger/pay/Api';
import type {
  CreatePaymentMethodForEntityParamsFlexible,
  DeactivatePaymentMethodForEntityParamsFlexible,
  DeactivatePaymentMethodParamsFlexible,
  DeletePaymentMethodForEntityParamsFlexible,
  DeletePaymentMethodParamsFlexible,
  ExchangeAuthorizedMethodsForEntityParamsFlexible,
  GetPaymentMethodForEntityParamsFlexible,
  GetPaymentMethodParamsFlexible,
  GetPaymentMethodsForEntityParamsFlexible,
  ReactivatePaymentMethodForEntityParamsFlexible,
  ReactivatePaymentMethodParamsFlexible,
  StartPaymentMethodSetupForEntityParamsFlexible,
  UpdatePaymentMethodForEntityParamsFlexible,
  UpdatePaymentMethodParamsFlexible,
} from '~/swagger/pay/payParams';

const repo: PayPaymentMethodsRepository = payPaymentMethodsRepository;

export const [PayPaymentMethodsProvider, usePayPaymentMethods] = createMagicDoorContext('PayPaymentMethods', () => {
  const [methods, setMethodsFilter, methodsActions] = createTriggerResource((params?: GetPaymentMethodsParams) =>
    repo.getPaymentMethods(params)
  );

  const [entityMethods, setEntityMethodsFilter, entityMethodsActions] = createTriggerResource(
    (params: GetPaymentMethodsForEntityParamsFlexible) => repo.getPaymentMethodsForEntity(params)
  );

  const [currentMethod, setCurrentMethodId, currentMethodActions] = createTriggerResource((params: GetPaymentMethodParamsFlexible) =>
    repo.getPaymentMethod(params)
  );

  const [currentEntityMethod, setCurrentEntityMethodParams, currentEntityMethodActions] = createTriggerResource(
    (params: GetPaymentMethodForEntityParamsFlexible) => repo.getPaymentMethodForEntity(params)
  );

  const [setupConfig, setSetupParams, setupActions] = createTriggerResource((body?: StartPaymentMethodSetupRequestDto) =>
    repo.startPaymentMethodSetup(body)
  );

  const [entitySetupConfig, setEntitySetupParams, entitySetupActions] = createTriggerResource(
    (params: StartPaymentMethodSetupForEntityParamsFlexible & { body?: StartPaymentMethodSetupRequestDto }) => {
      const { body, ...pathParams } = params;
      return repo.startPaymentMethodSetupForEntity(pathParams, body);
    }
  );

  const requestConnectedAccount = createMutation(async (body: RequestConnectedAccountRequestDto): Promise<ConnectedAccountDto> => {
    return repo.requestConnectedAccount(body);
  });

  const [connectedAccounts, setConnectedAccountsParams, connectedAccountsActions] = createTriggerResource(
    (params: { bankAccountIds: string[] }): Promise<ConnectedAccountDtoPaginationDto> => repo.getConnectedAccounts(params)
  );

  const createPaymentMethod = createMutation(
    async (
      body:
        | CreatePaymentMethodRequestDto
        | CreateTokenPaymentMethodRequestDto
        | CreateCardPaymentMethodRequestDto
        | CreateAchPaymentMethodRequestDto
    ): Promise<PaymentMethodDto> => {
      const result = await repo.createPaymentMethod(body);
      methodsActions.refetch();
      return result;
    }
  );

  const createPaymentMethodForEntity = createMutation(
    async (
      params: CreatePaymentMethodForEntityParamsFlexible,
      body:
        | CreatePaymentMethodRequestDto
        | CreateTokenPaymentMethodRequestDto
        | CreateCardPaymentMethodRequestDto
        | CreateAchPaymentMethodRequestDto
    ): Promise<PaymentMethodDto> => {
      const result = await repo.createPaymentMethodForEntity(params, body);
      entityMethodsActions.refetch();
      return result;
    }
  );

  const updatePaymentMethod = createMutation(
    async (params: UpdatePaymentMethodParamsFlexible, body: UpdatePaymentMethodRequestDto): Promise<PaymentMethodDto> => {
      const result = await repo.updatePaymentMethod(params, body);
      currentMethodActions.refetch();
      methodsActions.refetch();
      return result;
    }
  );

  const updatePaymentMethodForEntity = createMutation(
    async (params: UpdatePaymentMethodForEntityParamsFlexible, body: UpdatePaymentMethodRequestDto): Promise<PaymentMethodDto> => {
      const result = await repo.updatePaymentMethodForEntity(params, body);
      currentEntityMethodActions.refetch();
      entityMethodsActions.refetch();
      return result;
    }
  );

  const deletePaymentMethod = createMutation(async (params: DeletePaymentMethodParamsFlexible): Promise<void> => {
    await repo.deletePaymentMethod(params);
    methodsActions.refetch();
  });

  const deletePaymentMethodForEntity = createMutation(async (params: DeletePaymentMethodForEntityParamsFlexible): Promise<void> => {
    await repo.deletePaymentMethodForEntity(params);
    entityMethodsActions.refetch();
  });

  const deactivatePaymentMethod = createMutation(
    async (params: DeactivatePaymentMethodParamsFlexible, body: ReactivatePaymentMethodRequestDto): Promise<PaymentMethodDto> => {
      const result = await repo.deactivatePaymentMethod(params, body);
      currentMethodActions.refetch();
      methodsActions.refetch();
      return result;
    }
  );

  const deactivatePaymentMethodForEntity = createMutation(
    async (params: DeactivatePaymentMethodForEntityParamsFlexible, body: ReactivatePaymentMethodRequestDto): Promise<PaymentMethodDto> => {
      const result = await repo.deactivatePaymentMethodForEntity(params, body);
      currentEntityMethodActions.refetch();
      entityMethodsActions.refetch();
      return result;
    }
  );

  const reactivatePaymentMethod = createMutation(
    async (params: ReactivatePaymentMethodParamsFlexible, body?: ReactivatePaymentMethodRequestDto): Promise<PaymentMethodDto> => {
      const result = await repo.reactivatePaymentMethod(params, body);
      currentMethodActions.refetch();
      methodsActions.refetch();
      return result;
    }
  );

  const reactivatePaymentMethodForEntity = createMutation(
    async (params: ReactivatePaymentMethodForEntityParamsFlexible, body?: ReactivatePaymentMethodRequestDto): Promise<PaymentMethodDto> => {
      const result = await repo.reactivatePaymentMethodForEntity(params, body);
      currentEntityMethodActions.refetch();
      entityMethodsActions.refetch();
      return result;
    }
  );

  const exchangeAuthorizedMethods = createMutation(
    async (body: ExchangeAuthorizedMethodsRequestDto): Promise<ExchangedAuthorizedMethodsDto> => {
      return repo.exchangeAuthorizedMethods(body);
    }
  );

  const exchangeAuthorizedMethodsForEntity = createMutation(
    async (
      params: ExchangeAuthorizedMethodsForEntityParamsFlexible,
      body: ExchangeAuthorizedMethodsRequestDto
    ): Promise<ExchangedAuthorizedMethodsDto> => {
      return repo.exchangeAuthorizedMethodsForEntity(params, body);
    }
  );

  createEffect(() => {
    setMethodsFilter({});
  });

  return {
    // global methods
    methods,
    setMethodsFilter,
    methodsActions,
    currentMethod,
    setCurrentMethodId,
    currentMethodActions,
    setupConfig,
    setSetupParams,
    setupActions,
    // entity-scoped methods
    entityMethods,
    setEntityMethodsFilter,
    entityMethodsActions,
    currentEntityMethod,
    setCurrentEntityMethodParams,
    currentEntityMethodActions,
    entitySetupConfig,
    setEntitySetupParams,
    entitySetupActions,
    // mutations
    requestConnectedAccount,
    connectedAccounts,
    setConnectedAccountsParams,
    connectedAccountsActions,
    createPaymentMethod,
    createPaymentMethodForEntity,
    updatePaymentMethod,
    updatePaymentMethodForEntity,
    deletePaymentMethod,
    deletePaymentMethodForEntity,
    deactivatePaymentMethod,
    deactivatePaymentMethodForEntity,
    reactivatePaymentMethod,
    reactivatePaymentMethodForEntity,
    exchangeAuthorizedMethods,
    exchangeAuthorizedMethodsForEntity,
  };
});
